import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ExternalLink, Droplets, Brain, Music, Activity, Heart, Compass } from 'lucide-react';

// ════════════════════════════════════════════════════════════════
// Breathing Room — Services & Rates
// All services & prices available at Breathing Room (Hamilton, BMD)
// Source: WellnessLiving booking page (synced manually — update here when prices change)
// ════════════════════════════════════════════════════════════════

const BOOKING_URL =
  'https://www.wellnessliving.com/rs/appointment-start.html?id_class_tab=3&id_mode=1&k_business=731965';

const categories = [
  {
    id: 'dry-floats',
    name: 'Dry Floats',
    icon: Droplets,
    accent: 'text-[#4a7a7a]',
    services: [
      { name: '30 Minute Dry Float', duration: '30 min', price: '$70' },
      { name: '60 Minute Dry Float', duration: '1 hr', price: '$120' },
      { name: '30 Min Consult', duration: '30 min', price: 'Free' },
    ],
  },
  {
    id: 'ai-massage',
    name: 'AI Massage Chair',
    icon: Activity,
    accent: 'text-[#7a6a5a]',
    services: [
      { name: '30 Minute Massage Chair', duration: '30 min', price: '$40' },
    ],
  },
  {
    id: 'sound-bed',
    name: 'Sound Therapy Bed',
    icon: Music,
    accent: 'text-[#6a5a78]',
    services: [
      { name: 'Sound Therapy Bed', duration: '40 min', price: '$75' },
    ],
  },
  {
    id: 'movement',
    name: 'Movement & Personal Training',
    icon: Compass,
    accent: 'text-forest',
    services: [
      { name: '30 Minute Movement Session', duration: '30 min', price: '$70' },
      { name: '45 Minute Movement Session', duration: '45 min', price: '$80 – $90' },
      { name: '60 Minute Movement Session', duration: '60 min', price: '$110' },
    ],
  },
  {
    id: 'restorative',
    name: 'Restorative Services',
    icon: Heart,
    accent: 'text-terracotta',
    services: [
      { name: 'Reiki', duration: '', price: '$75' },
      { name: 'Colombian Wood Therapy', duration: '30 min', price: '$75' },
      { name: 'Body / Emotion Alignment Session', duration: '1 hr', price: '$125' },
      { name: 'Red Light Wand', duration: '15 min', price: '$35' },
      { name: 'Rife Frequency Therapy', duration: '45 min', price: '$50' },
      { name: 'Healy — Personalized Frequency Healing', duration: '1 hr', price: '$35 – $110' },
      { name: 'Ionic Foot Detox', duration: '', price: '$40' },
      { name: 'Tarot Session', duration: '', price: '$75' },
      { name: 'Pulsed Electromagnetic Frequency (PEMF)', duration: '45 min', price: '$75' },
      { name: 'Lymphatic Frequency Detox — Sinus', duration: '45 min', price: '$120' },
      { name: 'The Ultimate Experience Plus Package', duration: '2 hr 15 min', price: '$190' },
      { name: 'Breathing Room Services — Bundles & Memberships', duration: '1 hr', price: 'Purchase options available' },
    ],
  },
  {
    id: 'coaching',
    name: 'Coaching Services',
    icon: Brain,
    accent: 'text-[#8a6a5a]',
    services: [
      { name: 'Holistic Coaching', duration: '', price: '$110' },
      { name: '60 Minute Wellness Coaching', duration: '1 hr', price: '$110' },
      { name: 'Life Alignment Coaching', duration: '1 hr', price: '$110' },
      { name: 'Creating a Map to MY Wellness — 3 Session Series', duration: '1 hr 30 min', price: 'Purchase options available' },
    ],
  },
];

const BreathingRoomServices = () => {
  const [openId, setOpenId] = useState(null);

  return (
    <section className="py-12 md:py-16 bg-[#f7f1e8]/40" data-testid="breathing-room-services">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-10"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-body mb-3">
            At the Studio
          </p>
          <h2 className="font-heading text-3xl md:text-4xl text-forest mb-3">
            Breathing Room — Services & Rates
          </h2>
          <p className="text-stone-500 font-body max-w-2xl mx-auto leading-relaxed">
            A snapshot of all services offered at Breathing Room, the home studio. Tap any category
            to expand. Prices and durations sync with the live booking system.
          </p>
        </motion.div>

        <div className="space-y-3">
          {categories.map((cat) => {
            const Icon = cat.icon;
            const isOpen = openId === cat.id;
            return (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="bg-white border border-stone-200 rounded-xl overflow-hidden shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : cat.id)}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-stone-50/60 transition-colors"
                  data-testid={`br-cat-${cat.id}`}
                  aria-expanded={isOpen}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0 ${cat.accent}`}>
                      <Icon size={18} />
                    </span>
                    <div className="text-left min-w-0">
                      <p className="font-heading text-base sm:text-lg text-forest truncate">{cat.name}</p>
                      <p className="font-body text-xs text-stone-400">
                        {cat.services.length} service{cat.services.length === 1 ? '' : 's'}
                      </p>
                    </div>
                  </div>
                  <ChevronDown
                    size={18}
                    className={`text-stone-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-stone-100 divide-y divide-stone-100">
                        {cat.services.map((s, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-[#faf6ef]/70"
                          >
                            <p className="font-body text-sm text-stone-700">{s.name}</p>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              {s.duration && (
                                <span className="text-xs font-body text-stone-400 hidden sm:inline">{s.duration}</span>
                              )}
                              <span className={`text-sm font-body font-medium ${
                                s.price === 'Free' ? 'text-emerald-700' :
                                s.price.includes('Purchase') ? 'text-stone-500 italic text-xs' :
                                'text-forest'
                              }`}>
                                {s.price}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-8 text-center">
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-forest text-cream px-6 py-3 rounded-full font-body text-sm font-medium hover:bg-forest-dark transition-colors"
            data-testid="br-book-now"
          >
            Book any service on WellnessLiving
            <ExternalLink size={14} />
          </a>
          <p className="text-xs font-body text-stone-400 italic mt-3">
            Prices subject to change — confirm at booking.
          </p>
        </div>
      </div>
    </section>
  );
};

export default BreathingRoomServices;
